import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Clock, ArrowLeft, ChevronRight, CheckCircle, AlertTriangle, HelpCircle, MessageSquare, LogOut, Sparkles } from 'lucide-react';
import ChatBot from '../components/ChatBot';
import { useAuth } from '../contexts/AuthContext';

const SimulationPage = () => {
  const { id } = useParams<{ id: string }>();
  const [currentStep, setCurrentStep] = useState(0);
  const [userResponses, setUserResponses] = useState<string[]>([]);
  const [showChatbot, setShowChatbot] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(45 * 60); // 45 minutes in seconds
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // Simulation data (in a real app, this would come from an API)
  const simulation = {
    id: 'sim-1',
    title: 'Recorte de presupuesto',
    description: 'Tu presupuesto ha sido recortado un 30%. Decide qué funcionalidades priorizar y cuáles posponer.',
    context: 'Eres el Product Manager de una aplicación de productividad llamada TaskMaster. La empresa ha decidido recortar el presupuesto de desarrollo en un 30% debido a condiciones económicas. Debes decidir qué funcionalidades priorizar y cuáles posponer para el próximo trimestre.',
    difficulty: 'intermediate',
    duration: '45 min',
    type: 'decision',
    skills: ['Priorización', 'Negociación', 'Análisis de impacto'],
    steps: [
      {
        title: 'Análisis de la situación',
        description: 'Analiza la situación actual y el impacto del recorte de presupuesto.',
        task: 'Identifica las funcionalidades planificadas para el próximo trimestre y su importancia relativa.',
        options: [
          {
            id: 'a',
            text: 'Revisar el roadmap actual y clasificar las funcionalidades por impacto en usuarios',
            feedback: 'Buen enfoque. Priorizar basándose en el impacto en los usuarios es una estrategia sólida.',
            score: 8
          },
          {
            id: 'b',
            text: 'Analizar qué funcionalidades generarán más ingresos a corto plazo',
            feedback: 'Enfoque válido desde la perspectiva de negocio, pero considera también el valor para el usuario.',
            score: 7
          },
          {
            id: 'c',
            text: 'Identificar las funcionalidades más fáciles de implementar',
            feedback: 'Este enfoque prioriza la eficiencia, pero podría no alinearse con las necesidades del usuario o del negocio.',
            score: 5
          }
        ]
      },
      {
        title: 'Comunicación con stakeholders',
        description: 'Comunica el recorte de presupuesto a los stakeholders relevantes.',
        task: '¿Cómo comunicarías esta situación a los diferentes equipos involucrados?',
        options: [
          {
            id: 'a',
            text: 'Reunir a todos los stakeholders y explicar la situación con transparencia',
            feedback: 'Excelente. La transparencia y la comunicación abierta son clave en situaciones difíciles.',
            score: 9
          },
          {
            id: 'b',
            text: 'Comunicar solo a los líderes de equipo y pedirles que informen a sus equipos',
            feedback: 'Este enfoque puede funcionar, pero podría generar confusión o interpretaciones diferentes.',
            score: 6
          },
          {
            id: 'c',
            text: 'Enviar un email detallando los cambios sin reuniones adicionales',
            feedback: 'La comunicación escrita es clara, pero en situaciones complejas, la interacción directa es preferible.',
            score: 4
          }
        ]
      },
      {
        title: 'Toma de decisiones',
        description: 'Decide qué funcionalidades priorizar y cuáles posponer.',
        task: 'Basándote en tu análisis, ¿qué criterios utilizarías para tomar la decisión final?',
        options: [
          {
            id: 'a',
            text: 'Equilibrar el valor para el usuario, el impacto en el negocio y el esfuerzo de desarrollo',
            feedback: 'Excelente enfoque holístico que considera múltiples factores importantes.',
            score: 10
          },
          {
            id: 'b',
            text: 'Priorizar las funcionalidades con mayor retorno de inversión',
            feedback: 'Buen enfoque desde la perspectiva financiera, pero considera también otros factores.',
            score: 7
          },
          {
            id: 'c',
            text: 'Seguir las preferencias del CEO y los ejecutivos',
            feedback: 'Es importante considerar la visión de los líderes, pero un PM debe aportar su perspectiva basada en datos y usuarios.',
            score: 5
          }
        ]
      },
      {
        title: 'Plan de implementación',
        description: 'Desarrolla un plan para implementar las decisiones tomadas.',
        task: '¿Cómo implementarías los cambios en el roadmap y el proceso de desarrollo?',
        options: [
          {
            id: 'a',
            text: 'Crear un nuevo roadmap con hitos claros y comunicarlo a todos los equipos',
            feedback: 'Excelente. Un plan claro con hitos definidos ayuda a mantener a todos alineados.',
            score: 9
          },
          {
            id: 'b',
            text: 'Ajustar el sprint actual y continuar iterando sprint a sprint',
            feedback: 'Este enfoque ágil puede funcionar, pero asegúrate de tener una visión clara a largo plazo.',
            score: 7
          },
          {
            id: 'c',
            text: 'Dejar que cada equipo decida cómo ajustar sus propias prioridades',
            feedback: 'Dar autonomía es positivo, pero podría llevar a desalineación sin una dirección clara.',
            score: 4
          }
        ]
      }
    ]
  };

  const currentStepData = simulation.steps[currentStep];

  const handleOptionSelect = (optionId: string) => {
    const selectedOption = currentStepData.options.find(option => option.id === optionId);
    if (selectedOption) {
      const newResponses = [...userResponses];
      newResponses[currentStep] = optionId;
      setUserResponses(newResponses);
      
      if (currentStep < simulation.steps.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const calculateScore = () => {
    let totalScore = 0;
    userResponses.forEach((response, index) => {
      const step = simulation.steps[index];
      const option = step.options.find(opt => opt.id === response);
      if (option) {
        totalScore += option.score;
      }
    });
    return Math.round(totalScore / userResponses.length);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const isSimulationComplete = userResponses.length === simulation.steps.length;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Dashboard Header/Navbar */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Sparkles className="h-8 w-8 text-accent" />
                <span className="ml-2 text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600">RealMentor AI</span>
              </div>
            </div>
            <div className="flex items-center">
              <div className="flex items-center space-x-4">
                <Link to="/dashboard" className="text-accent hover:text-accent/90">
                  Dashboard
                </Link>
                <button
                  onClick={handleSignOut}
                  className="flex items-center text-gray-500 hover:text-gray-700 px-3 py-2 rounded-md text-sm font-medium"
                >
                  <LogOut className="h-4 w-4 mr-1" />
                  Cerrar sesión
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6">
          <Link to="/dashboard" className="inline-flex items-center text-accent hover:text-accent/90">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Volver al dashboard
          </Link>
        </div>

        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold">{simulation.title}</h1>
              <div className="flex items-center">
                <Clock className="h-5 w-5 mr-1" />
                <span>{formatTime(timeRemaining)}</span>
              </div>
            </div>
            <p className="mt-2">{simulation.description}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {simulation.skills.map((skill, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>

          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900">Contexto</h2>
              <p className="mt-2 text-gray-600">{simulation.context}</p>
            </div>

            <div className="mb-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">Progreso</h2>
                <button
                  onClick={() => setShowChatbot(true)}
                  className="inline-flex items-center text-sm text-accent hover:text-accent/90"
                >
                  <MessageSquare className="h-4 w-4 mr-1" />
                  Consultar a tu mentor
                </button>
              </div>
              <div className="mt-4">
                <div className="flex items-center">
                  <div className="flex-1">
                    <div className="h-2 bg-gray-200 rounded-full">
                      <div
                        className="h-2 bg-accent rounded-full"
                        style={{ width: `${(currentStep / simulation.steps.length) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  <span className="ml-2 text-sm text-gray-500">
                    {currentStep}/{simulation.steps.length}
                  </span>
                </div>
                <div className="mt-4 flex items-center space-x-2 overflow-x-auto py-2">
                  {simulation.steps.map((step, index) => (
                    <div
                      key={index}
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                        index < currentStep
                          ? 'bg-accent text-white'
                          : index === currentStep
                          ? 'bg-accent/20 text-accent border-2 border-accent'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {index + 1}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {!isSimulationComplete ? (
              <div className="border rounded-lg p-6">
                <h3 className="text-xl font-medium text-gray-900">
                  Paso {currentStep + 1}: {currentStepData.title}
                </h3>
                <p className="mt-2 text-gray-600">{currentStepData.description}</p>
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <HelpCircle className="h-5 w-5 text-yellow-400" />
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-yellow-800">Tu tarea</h4>
                      <p className="mt-1 text-sm text-yellow-700">{currentStepData.task}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                   <h4 className="text-sm font-medium text-gray-700">Selecciona una opción:</h4>
                  {currentStepData.options.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleOptionSelect(option.id)}
                      className={`w-full text-left p-4 border rounded-lg hover:bg-gray-50 transition-colors ${
                        userResponses[currentStep] === option.id
                          ? 'border-accent bg-accent/5'
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start">
                        <div className="flex-shrink-0 pt-0.5">
                          <div
                            className={`h-5 w-5 rounded-full border flex items-center justify-center ${
                              userResponses[currentStep] === option.id
                                ? 'border-accent bg-accent text-white'
                                : 'border-gray-300'
                            }`}
                          >
                            {userResponses[currentStep] === option.id && (
                              <CheckCircle className="h-3 w-3" />
                            )}
                          </div>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">{option.text}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="mt-6 flex justify-between">
                  <button
                    onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                    disabled={currentStep === 0}
                    className={`inline-flex items-center px-4 py-2 border rounded-md text-sm font-medium ${
                      currentStep === 0
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => {
                      if (userResponses[currentStep] && currentStep < simulation.steps.length - 1) {
                        setCurrentStep(currentStep + 1);
                      }
                    }}
                    disabled={!userResponses[currentStep] || currentStep === simulation.steps.length - 1}
                    className={`inline-flex items-center px-4 py-2 border rounded-md text-sm font-medium ${
                      !userResponses[currentStep] || currentStep === simulation.steps.length - 1
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'border-accent bg-accent text-white hover:bg-accent/90'
                    }`}
                  >
                    Siguiente
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="border rounded-lg p-6">
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="mt-3 text-lg font-medium text-gray-900">¡Simulación completada!</h3>
                  <div className="mt-4 p-4 bg-white border rounded-lg shadow-sm">
                    <h4 className="text-sm font-medium text-gray-700">Tu puntuación</h4>
                    <div className="mt-2 flex items-center justify-center">
                      <div className="text-5xl font-bold text-accent">{calculateScore()}</div>
                      <div className="ml-2 text-xl text-gray-500">/10</div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700">Resumen de tus decisiones</h4>
                    <div className="mt-4 space-y-4">
                      {simulation.steps.map((step, index) => {
                        const selectedOption = step.options.find(
                          (opt) => opt.id === userResponses[index]
                        );
                        return (
                          <div key={index} className="border rounded-lg p-4 text-left">
                            <h5 className="text-sm font-medium text-gray-900">{step.title}</h5>
                            <p className="mt-1 text-sm text-gray-600">
                              Tu elección: {selectedOption?.text}
                            </p>
                            <div className="mt-2 p-3 bg-blue-50 rounded-md">
                              <p className="text-sm text-blue-700">
                                <span className="font-medium">Feedback: </span>
                                {selectedOption?.feedback}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-6 flex justify-center space-x-4">
                    <Link
                      to="/dashboard"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Volver al dashboard
                    </Link>
                    <button
                      onClick={() => {
                        setCurrentStep(0);
                        setUserResponses([]);
                      }}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-accent hover:bg-accent/90"
                    >
                      Reintentar simulación
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showChatbot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <ChatBot role="product-manager" onClose={() => setShowChatbot(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default SimulationPage;